import React, { useState } from "react";

const COLORS = {
  bgCard: '#ffffff',
  bgSecondary: '#f8f9fa',
  textPrimary: '#1a1a1a',
  textSecondary: '#6c757d',
  textMuted: '#9ca3af',
  accent: '#6ab04c',
  accentGlow: 'rgba(106, 176, 76, 0.1)',
  border: 'rgba(0, 0, 0, 0.1)',
  danger: '#ef4444',
  warning: '#f59e0b',
  success: '#22c55e',
};

/**
 * Formulario unificado para agregar/editar veterinarios.
 * Se usa desde:
 *  - UserCalendarCitas (pantalla de citas)
 *  - ExpedienteClinicoVeterinarioList (módulo veterinario del menú avatar)
 *
 * Props:
 *  - veterinarianData: datos del vet existente (null para nuevo)
 *  - limites: { activos, maxActivos, invitacionesUsadas, maxInvitacionesAnio }
 *  - onSave: (formData) => void
 *  - onCancel: () => void
 *  - onReInvite: (vet) => void
 *  - isLoading: boolean
 */
export default function AgregarVeterinarioModal({
  veterinarianData = null,
  limites = null,
  onSave,
  onCancel,
  onReInvite,
  isLoading = false,
}) {
  const isExisting = !!veterinarianData?.id;
  const estado = veterinarianData?.estado || "pendiente";

  const [formData, setFormData] = useState({
    nombreClinica: veterinarianData?.nombreClinica || '',
    nombre: veterinarianData?.nombre || '',
    telefono: veterinarianData?.telefono || '',
    correo: veterinarianData?.correo || '',
    direccion: veterinarianData?.direccion || '',
    especialidad: veterinarianData?.especialidad || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!formData.nombre.trim()) return;
    const data = {
      ...formData,
      nombre: formData.nombre.trim(),
      nombreClinica: formData.nombreClinica.trim() || undefined,
      telefono: formData.telefono.trim() || undefined,
      correo: formData.correo.trim() || undefined,
      direccion: formData.direccion.trim() || undefined,
      especialidad: formData.especialidad.trim() || undefined,
    };
    if (!data.correo) {
      data.estado = "sinCorreo";
    }
    onSave(data);
  };

  const limitesAlcanzados = limites && limites.activos >= limites.maxActivos;
  const invitacionesAgotadas = limites && limites.invitacionesUsadas >= limites.maxInvitacionesAnio;
  const bloqueado = !isExisting && (limitesAlcanzados || invitacionesAgotadas);

  const inputStyle = {
    backgroundColor: COLORS.bgSecondary,
    color: COLORS.textPrimary,
    border: `1px solid ${COLORS.border}`,
  };

  // ── Estado verificado: solo lectura ──
  if (estado === "verificado") {
    return (
      <div style={{ backgroundColor: COLORS.bgCard, borderRadius: '12px', border: `1px solid ${COLORS.border}` }}>
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', margin: '0 auto 1rem',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <i className="bi bi-check-circle-fill" style={{ fontSize: '1.5rem', color: COLORS.success }}></i>
          </div>
          <h5 style={{ color: COLORS.textPrimary }}>Veterinario Conectado</h5>
          <p style={{ color: COLORS.textSecondary, fontSize: '0.9rem' }}>
            Este veterinario ya está <b>verificado</b>. Sus datos le pertenecen directamente.
          </p>
          <button
            className="btn"
            style={{ backgroundColor: 'transparent', color: COLORS.textSecondary, border: `1px solid ${COLORS.border}` }}
            onClick={onCancel}
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  // ── Estado rechazado ──
  if (estado === "noAceptado") {
    return (
      <div style={{ backgroundColor: COLORS.bgCard, borderRadius: '12px', border: `1px solid ${COLORS.border}` }}>
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', margin: '0 auto 1rem',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <i className="bi bi-x-circle-fill" style={{ fontSize: '1.5rem', color: COLORS.danger }}></i>
          </div>
          <h5 style={{ color: COLORS.textPrimary }}>Invitación Rechazada</h5>
          <p style={{ color: COLORS.textSecondary, fontSize: '0.9rem' }}>
            El veterinario <b>{veterinarianData?.nombre}</b> no aceptó la invitación.
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <button
              className="btn"
              style={{ backgroundColor: 'transparent', color: COLORS.textSecondary, border: `1px solid ${COLORS.border}` }}
              onClick={onCancel}
            >
              Cerrar
            </button>
            {onReInvite && (
              <button
                className="btn"
                style={{ backgroundColor: COLORS.warning, color: '#fff', border: 'none' }}
                onClick={() => onReInvite(veterinarianData)}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>Reinvitar
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Formulario principal (nuevo o edición) ──
  return (
    <div style={{ backgroundColor: COLORS.bgCard, borderRadius: '12px', border: `1px solid ${COLORS.border}` }}>
      <div style={{ padding: '1.5rem' }}>
        <h5 className="text-center mb-1" style={{ color: COLORS.textPrimary }}>
          <i className="bi bi-person-plus-fill me-2" style={{ color: COLORS.accent }}></i>
          {isExisting ? 'Editar Veterinario' : 'Agregar Veterinario'}
        </h5>
        {!isExisting && (
          <p className="text-center mb-3" style={{ color: COLORS.textSecondary, fontSize: '0.85rem' }}>
            Registra los datos de tu veterinario para invitarlo a conectarse
          </p>
        )}

        {/* Límites badges */}
        {limites && !isExisting && (
          <div className="d-flex justify-content-center gap-2 mb-3">
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.25rem 0.625rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 500,
              backgroundColor: limitesAlcanzados ? 'rgba(239, 68, 68, 0.1)' : COLORS.accentGlow,
              color: limitesAlcanzados ? COLORS.danger : COLORS.accent,
            }}>
              <i className="bi bi-people"></i>
              {limites.activos}/{limites.maxActivos} activos
            </span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.25rem 0.625rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 500,
              backgroundColor: invitacionesAgotadas ? 'rgba(239, 68, 68, 0.1)' : 'rgba(107, 114, 128, 0.1)',
              color: invitacionesAgotadas ? COLORS.danger : COLORS.textSecondary,
            }}>
              <i className="bi bi-envelope"></i>
              {limites.invitacionesUsadas}/{limites.maxInvitacionesAnio} invitaciones/año
            </span>
          </div>
        )}

        {/* Alertas de límites */}
        {!isExisting && limitesAlcanzados && (
          <div style={{
            backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#92400e'
          }}>
            <i className="bi bi-exclamation-triangle me-2"></i>
            Has alcanzado el límite de {limites.maxActivos} veterinarios activos. Elimina uno para agregar otro.
          </div>
        )}
        {!isExisting && invitacionesAgotadas && !limitesAlcanzados && (
          <div style={{
            backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#92400e'
          }}>
            <i className="bi bi-exclamation-triangle me-2"></i>
            Has usado tus {limites.maxInvitacionesAnio} invitaciones de este año.
            <br /><small><a href="mailto:soporte@lemmontags.com">Contacta a soporte</a> si necesitas más.</small>
          </div>
        )}

        {/* Nota sobre correo */}
        {!isExisting && (
          <div style={{
            backgroundColor: COLORS.bgSecondary, borderRadius: '8px',
            padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8rem', color: COLORS.textSecondary
          }}>
            <i className="bi bi-info-circle me-1"></i>
            Al indicar el <b>correo</b>, se le enviará una invitación. Si no lo conoces, déjalo en blanco.
          </div>
        )}

        {/* Campos del formulario */}
        <div className="mb-3">
          <label className="form-label fw-bold" style={{ color: COLORS.textPrimary, fontSize: '0.875rem' }}>
            <i className="bi bi-building me-1"></i> Nombre de la veterinaria
          </label>
          <input
            type="text" className="form-control" placeholder="Ej: Veterinaria San Marcos"
            value={formData.nombreClinica} onChange={(e) => handleChange('nombreClinica', e.target.value)}
            maxLength={100} style={inputStyle}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold" style={{ color: COLORS.textPrimary, fontSize: '0.875rem' }}>
            <i className="bi bi-person me-1"></i> Nombre del veterinario <span style={{ color: COLORS.danger }}>*</span>
          </label>
          <input
            type="text" className="form-control" placeholder="Ej: Dr. Juan Pérez"
            value={formData.nombre} onChange={(e) => handleChange('nombre', e.target.value)}
            maxLength={100} required style={inputStyle}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold" style={{ color: COLORS.textPrimary, fontSize: '0.875rem' }}>
            <i className="bi bi-telephone me-1"></i> Teléfono
          </label>
          <input
            type="tel" className="form-control" placeholder="Ej: 2281234567"
            value={formData.telefono} onChange={(e) => handleChange('telefono', e.target.value)}
            maxLength={15} style={inputStyle}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold" style={{ color: COLORS.textPrimary, fontSize: '0.875rem' }}>
            <i className="bi bi-envelope me-1"></i> Correo electrónico
          </label>
          <input
            type="email" className="form-control" placeholder="correo@ejemplo.com"
            value={formData.correo} onChange={(e) => handleChange('correo', e.target.value)}
            maxLength={100} style={inputStyle}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold" style={{ color: COLORS.textPrimary, fontSize: '0.875rem' }}>
            <i className="bi bi-geo-alt me-1"></i> Dirección
          </label>
          <input
            type="text" className="form-control" placeholder="Calle, Colonia, etc."
            value={formData.direccion} onChange={(e) => handleChange('direccion', e.target.value)}
            maxLength={200} style={inputStyle}
          />
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold" style={{ color: COLORS.textPrimary, fontSize: '0.875rem' }}>
            <i className="bi bi-award me-1"></i> Especialidad
          </label>
          <input
            type="text" className="form-control" placeholder="Ej: Cirugía, Oncología..."
            value={formData.especialidad} onChange={(e) => handleChange('especialidad', e.target.value)}
            maxLength={100} style={inputStyle}
          />
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn flex-grow-1"
            style={{ backgroundColor: 'transparent', color: COLORS.textSecondary, border: `1px solid ${COLORS.border}` }}
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            className="btn flex-grow-1"
            style={{
              backgroundColor: bloqueado ? COLORS.textMuted : COLORS.accent,
              color: '#fff', border: 'none'
            }}
            onClick={handleSubmit}
            disabled={!formData.nombre.trim() || isLoading || bloqueado}
          >
            {isLoading ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>Guardando...</>
            ) : (
              <><i className="bi bi-check-lg me-1"></i>{isExisting ? 'Guardar Cambios' : 'Agregar Veterinario'}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
